import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Moon, Sun } from "lucide-react";

export default function Auth() {
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") await register(email, password, name);
      else await login(email, password);
      navigate("/today");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen caden-grain bg-background flex flex-col md:flex-row">
      <button onClick={toggle} className="absolute top-5 right-5 z-20 p-2 rounded-full hover:bg-muted transition-colors text-muted-foreground" data-testid="theme-toggle-auth">
        {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      {/* Left: brand story */}
      <div className="relative hidden md:flex md:w-1/2 flex-col justify-between p-12 overflow-hidden bg-brand-black">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/8443237/pexels-photo-8443237.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Runner"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/70 to-brand-black/30" />
        </div>
        <div className="relative z-10">
          <Logo size={44} />
        </div>
        <div className="relative z-10 text-white">
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
            A coach that adapts<br />to <span className="text-brand-gradient">you</span>.
          </h1>
          <p className="mt-5 text-base text-white/70 max-w-md leading-relaxed">
            Not an athlete that adapts to a plan. Caden learns your life, your body and your goal — then makes the best decision for you, every day.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="md:hidden mb-8">
            <Logo size={40} />
          </div>
          <h2 className="font-heading font-extrabold text-3xl tracking-tight text-foreground">
            {mode === "register" ? "Meet your coach" : "Welcome back"}
          </h2>
          <p className="text-muted-foreground mt-2 mb-8">
            {mode === "register" ? "Create your account to start." : "Sign in to continue your training."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-1.5" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" />
            </div>

            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

            <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full h-12 text-base font-semibold rounded-xl active:scale-95 transition-transform">
              {loading ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "register" ? "Already have an account?" : "New here?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}
              className="text-primary font-semibold hover:underline"
            >
              {mode === "register" ? "Sign in" : "Create one"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
